<template>
  <v-app :key="userId">
    <!-- need to key app up in order to restart actioncable connection -->
    <svg-sprite />

    <v-btn
      v-if="!mobileNavigationVisible"
      class="mobile-menu-toggler"
      text
      @click="() => (mobileNavigationVisible = true)"
    >
      <v-icon color="primary">
        mdi-menu-close
      </v-icon>
    </v-btn>

    <v-navigation-drawer
      v-if="navigationDrawerVisible"
      v-model="navigationDrawerVisible"
      app
      v-bind="{
        temporary: isMobile,
        permanent: !isMobile
      }"
      dark
      color="#384457"
      width="202"
      :mini-variant="sidebarSmall"
      mini-variant-width="80"
      class="main-menu"
    >
      <Menu />

      <template #prepend>
        <SessionDetails :mini="sidebarSmall" @toggle="sidebarSmall = !sidebarSmall" />
      </template>

      <template #append>
        <LogoutSection v-if="isLoadedApp" :mini="sidebarSmall" />
      </template>
    </v-navigation-drawer>

    <v-main v-if="!isLoggedIn || isLoadedApp" :key="userId">
      <router-view :key="department && department.name" />
    </v-main>

    <ContextMenu />
    <Dialog v-if="$isLogisticsApp && isDialogActive" />
    <TheSnackbar />
    <template v-if="isLoadedApp && $isLogisticsApp">
      <ClientSearchDialog />
      <DriversOccupancy v-if="$route.name !== 'driversOccupancy'" />
      <OrderTemplateSnackbar />
      <TasksSnackbar />
      <TaskConfirmationSnackbar />
    </template>

    <v-overlay :value="isLoading" color="#f7f9fc" />

    <v-progress-linear :active="isLoading" :indeterminate="isLoading" absolute bottom height="6" />
  </v-app>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import SessionDetails from './components/Layout/Sidebar/SessionDetails.vue'
import LogoutSection from './components/Layout/Sidebar/LogoutSection.vue'
import ContextMenu from './components/Layout/ContextMenu'
import DriversOccupancy from './components/Layout/DriversOccupancy'
import ClientSearchDialog from './components/Layout/ClientSearchDialog'
import OrderTemplateSnackbar from './components/Layout/OrderTemplateSnackbar'
import TasksSnackbar from './components/Layout/TasksSnackbar'
import TaskConfirmationSnackbar from './components/Layout/TaskConfirmationSnackbar'
import Menu from './components/Layout/Menu'
import Dialog from './components/Layout/Dialog'
import SvgSprite from './components/Elements/Icons/SvgSprite'
import TheSnackbar from './components/Layout/TheSnackbar'
import { authorizeAxios } from './api/axios'

export default {
  name: 'App',
  components: {
    SessionDetails,
    LogoutSection,
    DriversOccupancy,
    Menu,
    ContextMenu,
    Dialog,
    TheSnackbar,
    SvgSprite,
    ClientSearchDialog,
    OrderTemplateSnackbar,
    TasksSnackbar,
    TaskConfirmationSnackbar
  },
  data() {
    return {
      sidebarSmall: false,
      mobileNavigationVisible: true,
      windowWidth: window.innerWidth
    }
  },
  computed: {
    ...mapState({
      isLoadedApp: state => state.core.isLoadedApp,
      isLoading: state => state.core.isLoading,
      department: state => state.core.department,
      isFullViewModeEnabled: state => state.core.fullViewMode,
      isDialogActive: state => state.layout.dialog.active
    }),
    userId() {
      return this.$store.state?.auth?.user?.id
    },
    isLoggedIn() {
      const module = this.$isLogisticsApp ? 'auth' : 'authClient'
      return this.$store.getters[`${module}/isLoggedIn`]
    },
    isMobile() {
      return this.windowWidth < 768
    },
    navigationDrawerVisible: {
      set(value) {
        this.mobileNavigationVisible = value
      },
      get() {
        if (this.isFullViewModeEnabled || !this.isLoggedIn || !this.isLoadedApp) return false
        return this.isMobile ? this.mobileNavigationVisible : true
      }
    }
  },
  created() {
    if (window.innerWidth < 768) this.sidebarSmall = true
    if (this.$route.query.full) this.setFullViewMode()
    if (this.$route.query.immediateInit) this.setImmediateInit()
    // Makes app content to be displayed immediately
    // there is still loader with overlay, but UX is better
    if (this.isLoggedIn) this.initApp()
    window.addEventListener('storage', this.setTokensOnLocalStorageChange)
  },
  mounted() {
    window.onresize = () => {
      this.windowWidth = window.innerWidth
      this.windowHeight = window.innerHeight
    }
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.setTokensOnLocalStorageChange)
  },
  methods: {
    ...mapActions({
      initApp: 'core/initApp',
      setFullViewMode: 'core/setFullViewMode',
      setImmediateInit: 'core/setAppLoadedState'
    }),
    setTokensOnLocalStorageChange(event) {
      if (event.key === 'EL-Auth-Token') {
        authorizeAxios({ token: event.newValue })
      }
      if (event.key === 'EL-Refresh-Token') {
        authorizeAxios({ refreshToken: event.newValue })
      }
    }
  }
}
</script>

<style lang="scss">
@import 'src/assets/styles/index.scss';
</style>
